import React, { useMemo, useState, useEffect } from 'react';
import { Chart } from 'primereact/chart';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import {LINE_COLORS, TIME_RANGE_OPTIONS} from '../../constants/dashboardDefaults';

const formatTime = (ts) =>
    new Date(ts * 1000).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const fmt = (val) => (val != null ? `${parseFloat(val).toFixed(1)} ms` : '—');

const PingDelayChart = ({ topNetworkPingDelay, loading, error, selectedHours, onRangeChange, onRefresh }) => {
    const series  = useMemo(() => topNetworkPingDelay?.series || [], [topNetworkPingDelay]);
    const hasData = series.length > 0;

    const [selectedDevice, setSelectedDevice] = useState(null);

    useEffect(() => { setSelectedDevice(null); }, [topNetworkPingDelay]);

    const toggleDevice = (deviceName) =>
        setSelectedDevice(prev => prev === deviceName ? null : deviceName);

    const maxLatency = useMemo(() => {
        const activeSeries = selectedDevice
            ? series.filter(s => s.deviceName === selectedDevice)
            : series;
        return Math.max(0, ...activeSeries.flatMap(s => s.points.map(p => p.latencyMs ?? 0)));
    }, [series, selectedDevice]);

    const { baseDatasets, labels } = useMemo(() => {
        const allTimestamps = [...new Set(
            series.flatMap(s => s.points.map(p => p.sourceRunTimestamp))
        )].sort((a, b) => a - b);

        const labels = allTimestamps.map(formatTime);

        const baseDatasets = series.map((device, i) => {
            const pointMap = Object.fromEntries(
                device.points.map(p => [p.sourceRunTimestamp, p.latencyMs])
            );
            return {
                label: device.deviceName,
                data: allTimestamps.map(ts => pointMap[ts] ?? null),
                borderColor: LINE_COLORS[i % LINE_COLORS.length],
                backgroundColor: LINE_COLORS[i % LINE_COLORS.length],
                tension: 0,
                pointRadius: 3,
                fill: false,
                spanGaps: true
            };
        });

        return { baseDatasets, labels };
    }, [series]);

    const chartData = useMemo(() => {
        if (!selectedDevice) return { labels, datasets: baseDatasets };

        const datasets = baseDatasets.map(ds => {
            const isSelected = ds.label === selectedDevice;
            return {
                ...ds,
                borderWidth:   isSelected ? 3 : 1,
                pointRadius:   isSelected ? 3 : 1,
                borderColor:   isSelected ? ds.borderColor : `${ds.borderColor}40`,   // 25% opacity when dimmed
                backgroundColor: isSelected ? ds.backgroundColor : `${ds.backgroundColor}40`,
                order: isSelected ? 0 : 1   // selected line renders on top
            };
        });
        return { labels, datasets };
    }, [selectedDevice, baseDatasets, labels]);

    const options = useMemo(() => ({
        plugins: { legend: { display: false } },
        scales: {
            x: { grid: { display: false }, ticks: { font: { size: 12 }, maxRotation: 45 } },
            y: {
                grid: { color: 'rgba(0,0,0,0.05)' },
                title: { display: true, text: 'Latency (ms)' },
                beginAtZero: true,
                suggestedMax: maxLatency > 0 ? maxLatency * 1.1 : 200,
                ticks: {
                    callback: (val) => {
                        if (val === 0) return '0';
                        if (val < 1000) return `${val}ms`;
                        return `${val / 1000}s`;
                    }
                }
            }
        },
        responsive: true,
        maintainAspectRatio: false,
        animation: { duration: 200 }
    }), [maxLatency]); 

    const renderEmpty = (icon, message, tall = true, isError = false) => (
        <div className={`chart-empty${tall ? ' chart-empty--tall' : ''}${isError ? ' chart-empty--error' : ''}`}>
            <i className={`pi ${icon}`} />
            <p>{message}</p>
        </div>
    );

    const renderContent = () => {
        if (loading) return (
            <div className="chart-empty chart-empty--tall">
                <ProgressSpinner style={{ width: '40px', height: '40px' }} strokeWidth="4" />
                <p>Loading...</p>
            </div>
        );
        if (error)                  return renderEmpty('pi-exclamation-circle', 'Failed to load data', true, true);
        if (!topNetworkPingDelay)   return renderEmpty('pi-chart-line',         'Search the DC to load data');
        if (!hasData)               return renderEmpty('pi-info-circle',         'No data found');

        return (
            <div className="ping-delay-split">
                {/* ── Line chart ── */}
                <div className="ping-delay-chart">
                    <div className="chart-wrapper chart-wrapper--tall">
                        <Chart type="line" data={chartData} options={options} data-testid="chart"/>
                    </div>
                </div>

                {/* ── Summary table ── */}
                <div className="ping-delay-summary">
                    <p className="ping-delay-summary__heading">
                        Top {series.length} Devices — Latency Summary
                    </p>
                    <div className="ping-summary-table-wrap">
                        <table className="ping-summary-table">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    <th>Device</th>
                                    <th>IP</th>
                                    <th>Min</th>
                                    <th>Avg</th>
                                    <th>Max</th>
                                </tr>
                            </thead>
                            <tbody>
                                {series.map((device, i) => {
                                    const isActive = selectedDevice === device.deviceName;
                                    const isDimmed = selectedDevice && !isActive;
                                    return (
                                        <tr
                                            key={device.deviceName}
                                            className={`ping-summary-row${isActive ? ' ping-summary-row--active' : ''}${isDimmed ? ' ping-summary-row--dimmed' : ''}`}
                                            onClick={() => toggleDevice(device.deviceName)}
                                            title={isActive ? 'Click to deselect' : 'Click to highlight'}
                                        >
                                            <td>
                                                <span
                                                    className="ping-summary-dot"
                                                    style={{ background: LINE_COLORS[i % LINE_COLORS.length] }}
                                                />
                                            </td>
                                            <td title={device.deviceName} className="ping-summary-name">
                                                {device.deviceName}
                                            </td>
                                            <td>{device.ipAddress}</td>
                                            <td>{fmt(device.summary?.minimumLatencyMs)}</td>
                                            <td>{fmt(device.summary?.averageLatencyMs)}</td>
                                            <td>{fmt(device.summary?.maximumLatencyMs)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div className="chart-card chart-card--full">
            <div className="chart-card-header">
                <h5 className="chart-title">Latency of Devices Over Time</h5>
                <div className="chart-controls">
                    <Dropdown
                        value={selectedHours}
                        options={TIME_RANGE_OPTIONS}
                        onChange={e => onRangeChange(e.value)}
                        className="chart-range-dropdown"
                        disabled={loading}
                    />
                    {onRefresh && (
                        <Button
                            icon="pi pi-refresh"
                            className="p-button-text p-button-sm chart-refresh-btn"
                            onClick={onRefresh}
                            disabled={loading}
                            tooltip="Refresh"
                            tooltipOptions={{ position: 'top' }}
                        />
                    )}
                </div>
            </div>
            {renderContent()}
        </div>
    );
};

export default PingDelayChart;


